import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';

// ── Layouts ──────────────────────────────────────────────────
import PublicLayout   from './components/layout/PublicLayout';
import TrainerLayout  from './components/layout/TrainerLayout';
import DirectorLayout from './components/layout/DirectorLayout';
import AdminLayout    from './components/layout/AdminLayout';

// ── Public pages ─────────────────────────────────────────────
import HomePage         from './pages/public/HomePage';
import OfferDetailPage  from './pages/public/OfferDetailPage';

// ── Auth pages ───────────────────────────────────────────────
import LoginPage    from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';

// ── Trainer pages ────────────────────────────────────────────
import MyCandidatures from './pages/trainer/MyCandidatures';
import ApplyForm      from './pages/trainer/ApplyForm';

// ── Director pages ───────────────────────────────────────────
import DirectorOffers       from './pages/director/DirectorOffers';
import DirectorCandidatures from './pages/director/DirectorCandidatures';
import DirectorModules      from './pages/director/DirectorModules';
import DirectorFilieres     from './pages/director/DirectorFilieres';
import DirectorSettings     from './pages/director/DirectorSettings';

// ── Admin pages ──────────────────────────────────────────────
import UsersPage   from './pages/admin/UsersPage';
import AdminIstas  from './pages/admin/AdminIstas';

// ── Shared pages ─────────────────────────────────────────────
import AccountPage from './pages/common/AccountPage';

// ── MUI Theme ────────────────────────────────────────────────
const theme = createTheme({
    palette: {
        primary: {
            main: '#1b263b',
            light: '#415a77',
            dark: '#0d1b2a',
            contrastText: '#fff',
        },
        secondary: {
            main: '#778da9',
            light: '#e0e1dd',
            dark: '#1b263b',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        success:   { main: '#10b981' },
        warning:   { main: '#f59e0b' },
        error:     { main: '#ef4444' },
        info:      { main: '#3b82f6' },
    },
    typography: {
        fontFamily: '"Outfit", "Inter", "Segoe UI", sans-serif',
        h4: { fontWeight: 800, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700, letterSpacing: '-0.01em' },
        h6: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { 
                    borderRadius: 10,
                    padding: '10px 20px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    },
                },
                containedPrimary: {
                    background: 'linear-gradient(135deg, #1b263b 0%, #415a77 100%)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                    },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    background: 'linear-gradient(90deg, #1b263b 0%, #415a77 100%)',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                },
            },
        },
    },
});


export default function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <BrowserRouter>
                    <Routes>

                        {/* ── Public (Visiteur) ──────────────────── */}
                        <Route element={<PublicLayout />}>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/offres/:id" element={<OfferDetailPage />} />
                        </Route>

                        {/* ── Auth ───────────────────────────────── */}
                        <Route path="/login"    element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />

                        {/* ── TRAINER ────────────────────────────── */}
                        <Route path="/trainer" element={
                            <ProtectedRoute roles={['trainer']}>
                                <TrainerLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Navigate to="candidatures" replace />} />
                            <Route path="candidatures" element={<MyCandidatures />} />
                            <Route path="postuler/:offerId" element={<ApplyForm />} />
                            <Route path="compte" element={<AccountPage />} />
                        </Route>

                        {/* ── DIRECTOR (RH) ─────────────────────── */}
                        <Route path="/director" element={
                            <ProtectedRoute roles={['director']}>
                                <DirectorLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Navigate to="offres" replace />} />
                            <Route path="offres"       element={<DirectorOffers />} />
                            <Route path="candidatures" element={<DirectorCandidatures />} />
                            <Route path="filieres"     element={<DirectorFilieres />} />
                            <Route path="modules"      element={<DirectorModules />} />
                            <Route path="parametres"   element={<DirectorSettings />} />
                            <Route path="compte"       element={<AccountPage />} />
                        </Route>

                        {/* ── ADMIN (Directeur — inherits director features) ── */}
                        <Route path="/admin" element={
                            <ProtectedRoute roles={['admin']}>
                                <AdminLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Navigate to="offres" replace />} />
                            <Route path="offres"       element={<DirectorOffers />} />
                            <Route path="candidatures" element={<DirectorCandidatures />} />
                            <Route path="filieres"     element={<DirectorFilieres />} />
                            <Route path="modules"      element={<DirectorModules />} />
                            <Route path="parametres"   element={<DirectorSettings />} />
                            <Route path="istas"        element={<AdminIstas />} />
                            <Route path="comptes"      element={<UsersPage />} />
                            <Route path="compte"       element={<AccountPage />} />
                        </Route>

                        {/* ── 404 → Homepage ─────────────────────── */}
                        <Route path="*" element={<Navigate to="/" replace />} />

                    </Routes>
                </BrowserRouter>

                <ToastContainer position="top-right" autoClose={3000} />
            </AuthProvider>
        </ThemeProvider>
    );
}